import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import type { Dayjs } from 'dayjs';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  BuiltInStateConfig,
  getRateAsOfDate,
  getSipTaxTableRows,
  isPremiumOnlyState,
  PREMIUM_ONLY_FEES_NOTE,
  SipTaxTableRow,
  SURPLUS_LINES_TAX_ESTIMATOR_TERMS_TEXT,
} from '../../helper/surplusLinesTaxEstimatorConfig';
import useAuth from '../../context/AuthProvider';
import { apiClient } from '../../helper/api';
import {
  calculateBuiltInEstimate,
  filterTaxEstimatorTransactionTypes,
  formatCurrency,
  TaxCalculatorResult,
  TaxEstimatorTransactionTypeOption,
  validateTaxCalculatorInputs,
} from '../../helper/surplusLinesTaxCalculator';
import {
  SL_ESTIMATOR_BLUE,
  SL_ESTIMATOR_SELECT_ARROW,
} from '../../helper/surplusLinesTaxEstimatorStyles';
import { BG_SUBTLE, BORDER, TEXT_PRIMARY, TEXT_SECONDARY } from './AdminHomeDashboardTile';

const sectionBoxSx = {
  border: `1px solid ${BORDER}`,
  borderRadius: '4px',
  mb: 2,
  overflow: 'hidden',
};

const fieldLabelSx = {
  fontSize: '0.9375rem',
  fontWeight: 500,
  color: SL_ESTIMATOR_BLUE,
  mb: 0.75,
};

const textInputSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#ffffff',
    borderRadius: '6px',
    '& fieldset': { borderColor: BORDER },
    '&:hover fieldset': { borderColor: SL_ESTIMATOR_BLUE },
    '&.Mui-focused fieldset': { borderColor: SL_ESTIMATOR_BLUE },
  },
  '& .MuiOutlinedInput-input::placeholder': {
    color: TEXT_SECONDARY,
    opacity: 0.7,
  },
};

const selectInputSx = {
  borderRadius: '6px',
  backgroundColor: '#ffffff',
  color: TEXT_PRIMARY,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: SL_ESTIMATOR_BLUE },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: SL_ESTIMATOR_BLUE },
  '& .MuiSelect-icon': { color: SL_ESTIMATOR_SELECT_ARROW },
};

const outlineButtonSx = {
  minWidth: 160,
  color: SL_ESTIMATOR_BLUE,
  borderColor: SL_ESTIMATOR_SELECT_ARROW,
  borderWidth: 2,
  borderRadius: '999px',
  px: 4,
  py: 1.25,
  fontWeight: 600,
  fontSize: '1rem',
  textTransform: 'none',
  backgroundColor: '#ffffff',
  boxShadow: 'none',
  '&:hover': {
    borderWidth: 2,
    borderColor: SL_ESTIMATOR_SELECT_ARROW,
    backgroundColor: 'rgba(32, 153, 210, 0.04)',
    boxShadow: 'none',
  },
};

const sipTableSx = {
  width: '100%',
  tableLayout: 'fixed',
  '& .MuiTableCell-root': {
    borderColor: BORDER,
    px: 1.75,
    py: 1.25,
    fontSize: '0.875rem',
    textAlign: 'left',
    verticalAlign: 'middle',
  },
  '& .MuiTableCell-head': {
    backgroundColor: BG_SUBTLE,
    color: TEXT_PRIMARY,
    fontWeight: 600,
    borderBottom: `1px solid ${BORDER}`,
  },
  '& .MuiTableCell-body': {
    backgroundColor: '#ffffff',
    color: TEXT_PRIMARY,
  },
  '& .MuiTableRow-root:last-of-type .MuiTableCell-body': {
    borderBottom: 'none',
  },
};

const EstimatorNoteText: React.FC<{ text: string }> = ({ text }) => (
  <>
    {text.split(/(\*\*[^*]+\*\*|__[^_]+__)/g).map((part, index) => {
      const boldMatch = part.match(/^\*\*([^*]+)\*\*$/);
      if (boldMatch) {
        return (
          <Typography
            key={index}
            component="strong"
            variant="inherit"
            sx={{ fontWeight: 700 }}
          >
            {boldMatch[1]}
          </Typography>
        );
      }
      const underlineMatch = part.match(/^__([^_]+)__$/);
      if (underlineMatch) {
        return (
          <Box key={index} component="span" sx={{ textDecoration: 'underline' }}>
            {underlineMatch[1]}
          </Box>
        );
      }
      return <React.Fragment key={index}>{part}</React.Fragment>;
    })}
  </>
);

const FootnoteAsterisk: React.FC = () => (
  <Box component="span" sx={{ color: '#e53935', fontWeight: 700, ml: 0.25 }}>
    *
  </Box>
);

const SipTaxTable: React.FC<{ rows: SipTaxTableRow[]; footnotes?: string[] }> = ({
  rows,
  footnotes,
}) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography
      sx={{
        fontSize: '0.9375rem',
        fontWeight: 600,
        color: TEXT_PRIMARY,
        mb: 1,
      }}
    >
      Tax Rate, Fee Rate &amp; Basis of Calculation
    </Typography>
    <Box
      sx={{
        border: `1px solid ${BORDER}`,
        borderRadius: '6px',
        overflow: 'hidden',
        backgroundColor: '#ffffff',
      }}
    >
      <Table size="small" sx={sipTableSx} aria-label="Tax Rate, Fee Rate and Basis of Calculation">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '40%' }}>Tax Type</TableCell>
            <TableCell sx={{ width: '28%' }}>Rate</TableCell>
            <TableCell>Taxable Items</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.taxType}>
              <TableCell>
                {row.taxType}
                {row.footnoteMarker && <FootnoteAsterisk />}
              </TableCell>
              <TableCell>
                {row.rate}
                {row.rateFootnoteMarker && <FootnoteAsterisk />}
              </TableCell>
              <TableCell>{row.taxableItems}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
    {footnotes?.map((footnote) => (
      <Typography
        key={footnote}
        sx={{
          mt: 1,
          fontSize: '0.8125rem',
          color: TEXT_SECONDARY,
          lineHeight: 1.5,
        }}
      >
        <FootnoteAsterisk />
        {' '}
        <EstimatorNoteText text={footnote} />
      </Typography>
    ))}
  </Box>
);

type SurplusLinesTaxEstimatorFormProps = {
  config: BuiltInStateConfig;
  termsAccepted: boolean;
  onCalculate: (result: TaxCalculatorResult, inputs: {
    premium: number;
    fees: number;
    policyEffectiveDate: string;
    endorsementEffectiveDate: string;
    transactionTypeId: number;
    transactionTypeName: string;
  }) => void;
};

const FormField: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box>
    <Typography sx={fieldLabelSx}>{label}</Typography>
    {children}
  </Box>
);

const toIsoDateOnly = (value: Dayjs | null): string =>
  (value && value.isValid() ? value.format('YYYY-MM-DD') : '');

const EstimateRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: 2,
      mb: 1.25,
    }}
  >
    <Typography sx={{ color: TEXT_SECONDARY, fontSize: '0.9375rem', flex: 1 }}>
      {label}
    </Typography>
    <Typography sx={{ color: SL_ESTIMATOR_BLUE, fontWeight: 600, fontSize: '0.9375rem', whiteSpace: 'nowrap' }}>
      {value}
    </Typography>
  </Box>
);

export const SurplusLinesTaxEstimatorForm: React.FC<SurplusLinesTaxEstimatorFormProps> = ({
  config,
  termsAccepted,
  onCalculate,
}) => {
  const { user } = useAuth();
  const premiumOnly = isPremiumOnlyState(config);
  const [policyEffectiveDate, setPolicyEffectiveDate] = useState<Dayjs | null>(null);
  const [transactionEffectiveDate, setTransactionEffectiveDate] = useState<Dayjs | null>(null);
  const [premium, setPremium] = useState('');
  const [fees, setFees] = useState('');
  const [transactionTypeId, setTransactionTypeId] = useState<number | ''>('');
  const [transactionTypes, setTransactionTypes] = useState<TaxEstimatorTransactionTypeOption[]>([]);
  const [transactionTypesLoading, setTransactionTypesLoading] = useState(true);
  const [transactionTypesError, setTransactionTypesError] = useState<string | null>(null);
  const [result, setResult] = useState<TaxCalculatorResult | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    const token = user?.token;
    if (!token) {
      setTransactionTypes([]);
      setTransactionTypesError('Unable to load transaction types.');
      setTransactionTypesLoading(false);
      return;
    }

    let cancelled = false;

    const fetchTransactionTypes = async () => {
      setTransactionTypesLoading(true);
      setTransactionTypesError(null);
      try {
        const responseJson = await apiClient('/TransactionType/getAllTransactionType', {
          method: 'GET',
          headers: new Headers({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          }),
        });

        if (cancelled) return;

        if (responseJson?.status && Array.isArray(responseJson.data) && responseJson.data.length > 0) {
          const filtered = filterTaxEstimatorTransactionTypes(
            responseJson.data as TaxEstimatorTransactionTypeOption[],
          );
          if (filtered.length === 0) {
            setTransactionTypes([]);
            setTransactionTypesError('Unable to load transaction types.');
          } else {
            setTransactionTypes(filtered);
          }
        } else {
          setTransactionTypes([]);
          setTransactionTypesError('Unable to load transaction types.');
        }
      } catch {
        if (!cancelled) {
          setTransactionTypes([]);
          setTransactionTypesError('Unable to load transaction types.');
        }
      } finally {
        if (!cancelled) {
          setTransactionTypesLoading(false);
        }
      }
    };

    fetchTransactionTypes();

    return () => {
      cancelled = true;
    };
  }, [user?.token]);

  const handleTransactionTypeChange = (event: SelectChangeEvent<string>) => {
    const nextValue = Number(event.target.value);
    setTransactionTypeId(Number.isInteger(nextValue) && nextValue > 0 ? nextValue : '');
  };

  const handleClear = () => {
    setPolicyEffectiveDate(null);
    setTransactionEffectiveDate(null);
    setPremium('');
    setFees('');
    setTransactionTypeId('');
    setResult(null);
    setErrors([]);
  };

  const handleSubmit = () => {
    const premiumValue = premium.trim() === '' ? NaN : Number(premium);
    const feesValue = premiumOnly || fees.trim() === '' ? 0 : Number(fees);
    const policyDateValue = toIsoDateOnly(policyEffectiveDate);
    const transactionDateValue = toIsoDateOnly(transactionEffectiveDate);
    const selectedTransactionType = transactionTypes.find(
      (option) => option.transactionId === transactionTypeId,
    );

    const validationErrors = validateTaxCalculatorInputs({
      premium: Number.isFinite(premiumValue) ? premiumValue : NaN,
      fees: Number.isFinite(feesValue) ? feesValue : NaN,
      policyEffectiveDate: policyDateValue || undefined,
      endorsementEffectiveDate: transactionDateValue || undefined,
      transactionTypeName: selectedTransactionType?.transactionTypeName,
    });

    if (!policyDateValue) {
      validationErrors.push({
        field: 'policyEffectiveDate',
        message: 'Policy effective date is required.',
      });
    }

    if (!transactionTypeId || !selectedTransactionType) {
      validationErrors.push({
        field: 'transactionTypeId',
        message: 'Transaction type is required.',
      });
    }

    if (!termsAccepted) {
      validationErrors.push({
        field: 'terms',
        message: 'You must accept the Terms & Conditions before calculating.',
      });
    }

    if (validationErrors.length > 0) {
      setErrors(validationErrors.map((e) => e.message));
      setResult(null);
      return;
    }

    const estimate = calculateBuiltInEstimate(config.stateName, {
      premium: premiumValue,
      fees: feesValue,
      policyEffectiveDate: policyDateValue || undefined,
      endorsementEffectiveDate: transactionDateValue || undefined,
      transactionTypeName: selectedTransactionType?.transactionTypeName,
    });

    if (!estimate) {
      setErrors(['Unable to calculate estimate for the selected state.']);
      setResult(null);
      return;
    }

    setErrors([]);
    setResult(estimate);
    onCalculate(estimate, {
      premium: premiumValue,
      fees: feesValue,
      policyEffectiveDate: policyDateValue,
      endorsementEffectiveDate: transactionDateValue,
      transactionTypeId: Number(transactionTypeId),
      transactionTypeName: selectedTransactionType?.transactionTypeName ?? '',
    });
  };

  const stateNotes = useMemo(() => {
    const notes: string[] = [];
    if (premiumOnly) {
      notes.push(PREMIUM_ONLY_FEES_NOTE);
    }
    const configuredNote = config.notes ?? config.phase1Limitation;
    if (configuredNote) {
      notes.push(configuredNote);
    }
    return notes;
  }, [config, premiumOnly]);

  const rateAsOfDate = useMemo(
    () => getRateAsOfDate(config, {
      policyEffectiveDate: toIsoDateOnly(policyEffectiveDate) || undefined,
      endorsementEffectiveDate: toIsoDateOnly(transactionEffectiveDate) || undefined,
    }),
    [config, policyEffectiveDate, transactionEffectiveDate],
  );

  const sipTableRows = useMemo(
    () => getSipTaxTableRows(config, rateAsOfDate),
    [config, rateAsOfDate],
  );

  return (
    <Box sx={{ maxWidth: '760px', mx: 'auto', width: '100%' }}>
      <Typography
        sx={{
          fontSize: '1.75rem',
          fontWeight: 700,
          color: TEXT_PRIMARY,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          mb: 1,
        }}
      >
        {config.stateName}
      </Typography>

      {sipTableRows.length > 0 && (
        <SipTaxTable rows={sipTableRows} footnotes={config.tableFootnotes} />
      )}

      {stateNotes.map((note) => (
        <Alert
          key={note}
          severity="info"
          sx={{
            mb: 2,
            '& strong': { fontWeight: 700 },
          }}
        >
          <EstimatorNoteText text={note} />
        </Alert>
      ))}

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <FormField label="Policy Effective Date:">
              <DatePicker
                inputFormat="MM/DD/YYYY"
                value={policyEffectiveDate}
                onChange={(value) => setPolicyEffectiveDate(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    placeholder="MM/DD/YYYY"
                    sx={textInputSx}
                  />
                )}
              />
            </FormField>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormField label="Transaction Effective Date:">
              <DatePicker
                inputFormat="MM/DD/YYYY"
                value={transactionEffectiveDate}
                onChange={(value) => setTransactionEffectiveDate(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    placeholder="MM/DD/YYYY"
                    sx={textInputSx}
                  />
                )}
              />
            </FormField>
          </Grid>

        <Grid item xs={12} md={6}>
          <FormField label="Gross/Total Premium:">
            <TextField
              fullWidth
              type="number"
              placeholder="Enter premium"
              inputProps={{ step: '0.01' }}
              value={premium}
              onChange={(e) => setPremium(e.target.value)}
              sx={textInputSx}
              size="small"
            />
          </FormField>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormField label="Total Taxable Fees:">
            <TextField
              fullWidth
              type="number"
              placeholder={premiumOnly ? 'Included in premium' : 'Enter taxable fees'}
              inputProps={{ step: '0.01' }}
              value={premiumOnly ? '' : fees}
              onChange={(e) => setFees(e.target.value)}
              disabled={premiumOnly}
              sx={{
                ...textInputSx,
                ...(premiumOnly && {
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#e8e8e8',
                    borderRadius: '6px',
                    '& fieldset': { borderColor: BORDER },
                  },
                }),
              }}
              size="small"
            />
          </FormField>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormField label="Transaction Type:">
            <FormControl fullWidth size="small">
              <Select
                value={transactionTypeId === '' ? '' : String(transactionTypeId)}
                onChange={handleTransactionTypeChange}
                displayEmpty
                disabled={transactionTypesLoading || transactionTypes.length === 0}
                IconComponent={ExpandMoreIcon}
                sx={selectInputSx}
                renderValue={(selected) => {
                  if (!selected) {
                    return (
                      <Typography sx={{ color: TEXT_SECONDARY, opacity: 0.7 }}>
                        {transactionTypesLoading
                          ? 'Loading…'
                          : transactionTypesError ?? 'Select Transaction Type'}
                      </Typography>
                    );
                  }
                  const selectedOption = transactionTypes.find(
                    (option) => String(option.transactionId) === selected,
                  );
                  return selectedOption?.transactionTypeName ?? selected;
                }}
              >
                {transactionTypes.map((option) => (
                  <MenuItem key={option.transactionId} value={String(option.transactionId)}>
                    {option.transactionTypeName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {transactionTypesError && !transactionTypesLoading && (
              <Typography sx={{ color: 'error.main', fontSize: '0.8125rem', mt: 0.5 }}>
                {transactionTypesError}
              </Typography>
            )}
          </FormField>
        </Grid>
        </Grid>
      </LocalizationProvider>

      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, mb: 3 }}>
        <Button variant="outlined" onClick={handleSubmit} sx={outlineButtonSx}>
          Submit
        </Button>
        <Button variant="outlined" onClick={handleClear} sx={outlineButtonSx}>
          Clear
        </Button>
      </Box>

      {errors.length > 0 && (
        <Box sx={{ mb: 2 }}>
          {errors.map((message) => (
            <Alert key={message} severity="error" sx={{ mb: 1 }}>
              {message}
            </Alert>
          ))}
        </Box>
      )}

      {result && (
        <Box sx={sectionBoxSx}>
          <Box sx={{ p: 2, pb: 1.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: SL_ESTIMATOR_BLUE, mb: 2 }}>
              Estimate:
            </Typography>

            <EstimateRow
              label="Estimated Taxable Premium:"
              value={formatCurrency(result.taxablePremium)}
            />

            {result.lines.map((line) => (
              <EstimateRow
                key={line.label}
                label={`${line.label}:`}
                value={formatCurrency(line.amount)}
              />
            ))}
          </Box>

          <Box
            sx={{
              px: 2,
              py: 1.75,
              backgroundColor: SL_ESTIMATOR_BLUE,
              color: '#ffffff',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 2,
                mb: 0.75,
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem', letterSpacing: '0.04em' }}>
                ESTIMATED TAXES &amp; FEES:
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem' }}>
                {formatCurrency(result.total)}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 2,
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', letterSpacing: '0.04em' }}>
                TOTAL PREMIUM:
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '1.125rem' }}>
                {formatCurrency(result.grandTotal)}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      <Typography
        component="p"
        sx={{
          mt: 2,
          fontSize: '0.9375rem',
          color: TEXT_SECONDARY,
          lineHeight: 1.5,
        }}
      >
        <Box component="span" sx={{ fontWeight: 500, color: SL_ESTIMATOR_BLUE }}>
          DISCLAIMER:
        </Box>{' '}
        {SURPLUS_LINES_TAX_ESTIMATOR_TERMS_TEXT}
      </Typography>
    </Box>
  );
};

export default SurplusLinesTaxEstimatorForm;
